import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Camera, X, FlashlightOff, Flashlight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function BarcodeScanner({ open, onOpenChange, onScan }) {
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  useEffect(() => {
    if (open) {
      // Get available cameras
      Html5Qrcode.getCameras()
        .then((devices) => {
          if (devices && devices.length) {
            setCameras(devices);
            // Prefer back camera
            const backCamera = devices.find(
              (d) => d.label.toLowerCase().includes("back") || 
                     d.label.toLowerCase().includes("trasera") ||
                     d.label.toLowerCase().includes("rear")
            );
            setSelectedCamera(backCamera || devices[0]);
          } else {
            setError("No se encontraron cámaras disponibles");
          }
        })
        .catch((err) => {
          console.error("Error getting cameras:", err);
          setError("No se pudo acceder a la cámara. Verifica los permisos.");
        });
    }

    return () => {
      stopScanner();
    };
  }, [open]);

  useEffect(() => {
    if (open && selectedCamera && !isScanning) {
      startScanner();
    }
  }, [selectedCamera, open]);

  const startScanner = async () => {
    if (!selectedCamera || !scannerRef.current) return;

    try {
      setError(null);
      
      if (html5QrCodeRef.current) {
        await html5QrCodeRef.current.stop();
      }

      html5QrCodeRef.current = new Html5Qrcode("barcode-scanner-region");
      
      await html5QrCodeRef.current.start(
        selectedCamera.id,
        {
          fps: 10,
          qrbox: { width: 250, height: 150 },
          aspectRatio: 1.777,
          formatsToSupport: [
            0, // QR_CODE
            1, // AZTEC
            2, // CODABAR
            3, // CODE_39
            4, // CODE_93
            5, // CODE_128
            6, // DATA_MATRIX
            7, // MAXICODE
            8, // ITF
            9, // EAN_13
            10, // EAN_8
            11, // PDF_417
            12, // RSS_14
            13, // RSS_EXPANDED
            14, // UPC_A
            15, // UPC_E
            16, // UPC_EAN_EXTENSION
          ],
        },
        (decodedText, decodedResult) => {
          // Success callback
          console.log("Barcode scanned:", decodedText);
          handleSuccessfulScan(decodedText);
        },
        (errorMessage) => {
          // Error callback - ignore, it fires constantly when no barcode is in view
        }
      );
      
      setIsScanning(true);
    } catch (err) {
      console.error("Error starting scanner:", err);
      setError("Error al iniciar el escáner: " + err.message);
    }
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current && isScanning) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current = null;
      } catch (err) {
        console.error("Error stopping scanner:", err);
      }
    }
    setIsScanning(false);
  };

  const handleSuccessfulScan = async (barcode) => {
    // Vibrate if available
    if (navigator.vibrate) {
      navigator.vibrate(200);
    }
    
    await stopScanner();
    onScan(barcode);
    onOpenChange(false);
  };

  const handleClose = async () => {
    await stopScanner();
    onOpenChange(false);
  };

  const switchCamera = () => {
    if (cameras.length > 1) {
      const currentIndex = cameras.findIndex((c) => c.id === selectedCamera?.id);
      const nextIndex = (currentIndex + 1) % cameras.length;
      stopScanner().then(() => {
        setSelectedCamera(cameras[nextIndex]);
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md p-0 overflow-hidden">
        <DialogHeader className="p-4 pb-2">
          <DialogTitle className="flex items-center gap-2">
            <Camera size={24} />
            Escanear Código de Barras
          </DialogTitle>
        </DialogHeader>
        
        <div className="relative">
          {/* Scanner region */}
          <div 
            id="barcode-scanner-region" 
            ref={scannerRef}
            className="w-full bg-black min-h-[300px]"
          />
          
          {/* Overlay with scanning frame */}
          {isScanning && (
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-64 h-32 border-2 border-white rounded-lg shadow-lg">
                  <div className="absolute top-0 left-0 w-4 h-4 border-t-4 border-l-4 border-green-500 rounded-tl" />
                  <div className="absolute top-0 right-0 w-4 h-4 border-t-4 border-r-4 border-green-500 rounded-tr" />
                  <div className="absolute bottom-0 left-0 w-4 h-4 border-b-4 border-l-4 border-green-500 rounded-bl" />
                  <div className="absolute bottom-0 right-0 w-4 h-4 border-b-4 border-r-4 border-green-500 rounded-br" />
                </div>
              </div>
              <div className="absolute bottom-4 left-0 right-0 text-center">
                <p className="text-white text-sm bg-black/50 inline-block px-3 py-1 rounded">
                  Apunta al código de barras
                </p>
              </div>
            </div>
          )}
          
          {/* Error message */}
          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/80">
              <div className="text-center p-4">
                <p className="text-red-400 mb-4">{error}</p>
                <Button onClick={startScanner} variant="outline">
                  Reintentar
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="p-4 flex justify-between items-center bg-slate-100">
          <div className="text-sm text-slate-600">
            {isScanning ? (
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                Escaneando...
              </span>
            ) : (
              "Preparando cámara..."
            )}
          </div>
          <div className="flex gap-2">
            {cameras.length > 1 && (
              <Button
                data-testid="switch-camera-btn"
                variant="outline"
                size="sm"
                onClick={switchCamera}
              >
                Cambiar cámara
              </Button>
            )}
            <Button
              data-testid="close-scanner-btn"
              variant="outline"
              size="sm"
              onClick={handleClose}
            >
              <X size={16} className="mr-1" />
              Cerrar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
